import dayjs, { isDayjs } from 'dayjs';

export const formatDate = ({
  date,
  defaultText,
  locale = 'en-GB',
  hasTime,
}: {
  date: Date | undefined;
  defaultText?: string;
  locale?: string;
  hasTime?: boolean;
}): string => {
  if (!date) return defaultText ?? '';

  const dateStr = date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (hasTime) {
    const timeStr = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${dateStr} - ${timeStr}`;
  }

  return dateStr;
};

// Converts a date string (YYYY-MM-DD or ISO 8601) or Date object to a Date object
export const getDate = (dateInput: Date | string | undefined): Date | undefined => {
  if (typeof dateInput === 'string') {
    return new Date(dateInput);
  }
  return dateInput;
};

// Recursively convert dates to ISO string format
export const convertDatesToISO = (obj: any, format = 'YYYY-MM-DDTHH:mm:ssZ'): any => {
  if (!obj) {
    return obj;
  }

  // Check if it's a Date object
  if (obj instanceof Date) {
    return dayjs(obj).format(format);
  }

  // Check if it's a dayjs object
  if (isDayjs(obj)) {
    return obj.format(format);
  }

  // If it's an array, recursively process each element
  if (Array.isArray(obj)) {
    return obj.map((item) => convertDatesToISO(item, format));
  }

  // If it's an object, recursively process each property
  if (typeof obj === 'object') {
    const converted: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        converted[key] = convertDatesToISO(obj[key], format);
      }
    }
    return converted;
  }

  // Return primitive values as-is
  return obj;
};

export const isCurrentDay = (date?: Date): boolean => {
  if (!date) return false;
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};
